import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { GBP, pretty } from "@/lib/helpers";
import { StatCard, PageHeader } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { PhoneCall, MessageSquare, DollarSign, ShieldAlert, Wallet, AlertTriangle } from "lucide-react";

export default function Usage() {
  const { rid } = useParams();
  const [usage, setUsage] = useState(null);
  const [sms, setSms] = useState([]);
  const [allergen, setAllergen] = useState([]);
  const [billing, setBilling] = useState(null);
  const [packages, setPackages] = useState([]);
  const [payMsg, setPayMsg] = useState(null);

  const loadBilling = () => api.get(`/restaurants/${rid}/billing`).then((r) => setBilling(r.data)).catch(() => {});

  useEffect(() => {
    api.get(`/restaurants/${rid}/ai-usage`).then((r) => setUsage(r.data));
    api.get(`/restaurants/${rid}/sms-logs`).then((r) => setSms(r.data));
    api.get(`/restaurants/${rid}/allergen-logs`).then((r) => setAllergen(r.data));
    api.get(`/payments/topup-packages`).then((r) => setPackages(r.data.packages)).catch(() => {});
    loadBilling();
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (sid) {
      setPayMsg("Checking payment…");
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const { data } = await api.get(`/payments/status/${sid}`);
          if (data.payment_status === "paid") { setPayMsg("Payment successful — credit added."); loadBilling(); return; }
          if (data.status === "expired") { setPayMsg("Payment session expired."); return; }
        } catch {}
        if (attempts < 6) setTimeout(poll, 2000); else setPayMsg("Payment still processing — refresh shortly.");
      };
      poll();
      window.history.replaceState({}, "", `/r/${rid}/usage`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rid]);

  const startTopup = async (pkgId) => {
    try {
      const { data } = await api.post(`/payments/checkout/topup`, { restaurant_id: rid, package_id: pkgId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch { toast.error("Could not start payment"); }
  };

  const lowBalance = billing && billing.credit_balance < 5;

  return (
    <div>
      <PageHeader title="Usage & cost" subtitle="AI call volume, credit balance, SMS logs and allergen disclosure records." />
      {lowBalance && (
        <div data-testid="low-balance-warning" className="mb-4 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <AlertTriangle className="h-4 w-4" /> Your credit balance is low. Top up to keep the AI receptionist active.
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Credit balance" value={billing ? GBP(billing.credit_balance) : "—"} icon={Wallet} accent="bg-emerald-100 text-emerald-700" testid="credit-balance-card" />
        <StatCard label="Total calls" value={usage?.total_calls ?? "—"} icon={PhoneCall} />
        <StatCard label="Total SMS" value={usage?.total_sms ?? "—"} icon={MessageSquare} />
        <StatCard label="Charges to date" value={billing ? GBP(billing.customer_charge) : "—"} icon={DollarSign} accent="bg-blue-100 text-blue-700" />
      </div>

      <Tabs defaultValue="billing">
        <TabsList>
          <TabsTrigger value="billing" data-testid="tab-billing">Billing</TabsTrigger>
          <TabsTrigger value="sms" data-testid="tab-sms">SMS logs</TabsTrigger>
          <TabsTrigger value="allergen" data-testid="tab-allergen">Allergen disclosure logs</TabsTrigger>
        </TabsList>
        <TabsContent value="billing" className="mt-4 space-y-4">
          <Card className="p-5 rounded-2xl" data-testid="topup-panel">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="font-display font-semibold">Top up credit</p>
                <p className="text-sm text-muted-foreground">Add prepaid credit — used for AI calls & SMS. Paid securely via Stripe.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {packages.map((p) => (
                  <Button key={p.id} variant="outline" className="h-11" data-testid={`topup-${p.id}`} onClick={() => startTopup(p.id)}>
                    +£{p.amount}
                  </Button>
                ))}
              </div>
            </div>
            {payMsg && <p className="text-sm mt-3 text-primary" data-testid="payment-status-msg">{payMsg}</p>}
          </Card>
          {billing?.transactions?.length ? billing.transactions.map((t) => (
            <Card key={t.id} className="p-4 rounded-xl flex items-center justify-between">
              <div>
                <p className="font-medium">{t.description}</p>
                <p className="text-sm text-muted-foreground">{new Date(t.created_at).toLocaleString()} · Balance {GBP(t.balance_after)}</p>
              </div>
              <span className={`font-display font-bold ${t.type === "CREDIT" ? "text-emerald-600" : "text-red-600"}`}>
                {t.type === "CREDIT" ? "+" : "−"}{GBP(t.amount)}
              </span>
            </Card>
          )) : <p className="text-muted-foreground">No credit transactions yet. Ask your platform admin to top up credit.</p>}
        </TabsContent>
        <TabsContent value="sms" className="mt-4 space-y-2">
          {sms.map((s) => (
            <Card key={s.id} className="p-4 rounded-xl flex items-center justify-between">
              <div>
                <p className="font-medium">{s.customer_phone} · {pretty(s.message_type)}</p>
                <p className="text-sm text-muted-foreground line-clamp-1">{s.message_body}</p>
              </div>
              <Badge variant="outline">{pretty(s.status)}</Badge>
            </Card>
          ))}
          {sms.length === 0 && <p className="text-muted-foreground">No SMS sent yet.</p>}
        </TabsContent>
        <TabsContent value="allergen" className="mt-4 space-y-2">
          {allergen.map((a) => (
            <Card key={a.id} className="p-4 rounded-xl">
              <div className="flex items-center gap-2">
                <ShieldAlert className="h-4 w-4 text-orange-600" />
                <p className="font-medium">{a.customer_phone || "In-house"}</p>
                {a.handover_triggered && <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Handover</Badge>}
              </div>
              <p className="text-sm text-muted-foreground mt-1">{a.disclosure_text}</p>
              {a.customer_response && <p className="text-xs text-muted-foreground mt-1">Response: {a.customer_response}</p>}
            </Card>
          ))}
          {allergen.length === 0 && <p className="text-muted-foreground">No allergen disclosures logged yet.</p>}
        </TabsContent>
      </Tabs>
    </div>
  );
}
